import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  Container,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  MenuOpen as MenuOpenIcon,
  Palette as PaletteIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  DesignServices as DesignIcon,
  SwapHoriz as SwapIcon,
  Groups as GroupsIcon,
  CardGiftcard as CardIcon,
  Warning as WarningIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as AccountBalanceIcon,
  Build as ToolsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../lib/api';
import StripeConnect from '../StripeConnect';
import PaymentForm from '../PaymentForm';
import Breadcrumbs from '../ui/Breadcrumbs';
import useBreadcrumbs from '../../hooks/useBreadcrumbs';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import theme from '../../theme';

if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
  throw new Error('REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable is not set');
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const normalizePassareLocation = (branch, fallbackIndex) => {
  const branchUuid = branch?.branchUuid || branch?.branch_uuid || `passare-${fallbackIndex}`;
  const branchName = branch?.branchName || branch?.branch_name || `Location ${fallbackIndex + 1}`;
  const nestedAddress = branch?.address || {};

  const addressLine1 = (
    branch?.addressLine1 ||
    branch?.address1 ||
    nestedAddress?.addressLine1 ||
    nestedAddress?.address1 ||
    nestedAddress?.line1 ||
    ''
  ).trim();
  const city = (branch?.city || nestedAddress?.city || '').trim();
  const state = (branch?.state || nestedAddress?.state || '').trim();
  const zipCode = (
    branch?.zipCode ||
    branch?.postalCode ||
    branch?.zip ||
    nestedAddress?.zipCode ||
    nestedAddress?.postalCode ||
    nestedAddress?.zip ||
    ''
  ).trim();
  const hasAddress = Boolean(addressLine1 || city || state || zipCode);

  return {
    branchUuid,
    branchName,
    addressLine1,
    city,
    state,
    zipCode,
    hasAddress,
    addressLabel: hasAddress
      ? [addressLine1, [city, state].filter(Boolean).join(', '), zipCode].filter(Boolean).join(', ')
      : 'No address provided by Passare',
  };
};

const normalizePassareOrganization = (organization, fallbackIndex) => {
  const organizationUuid =
    organization?.organizationUuid || organization?.organization_uuid || `passare-org-${fallbackIndex + 1}`;
  const organizationName =
    organization?.organizationName || organization?.organization_name || `Passare account ${fallbackIndex + 1}`;
  const branches = (organization?.branches || []).map((branch, index) => normalizePassareLocation(branch, index));

  return {
    organizationUuid,
    organizationName,
    branches,
  };
};

const getActiveBranchCount = (branches = []) =>
  branches.filter((branch) => (branch?.active ?? branch?.isActive ?? true) !== false).length;
const BRANCHES_PAGE_LIMIT = 100;

// Create a theme instance for the dashboard with IMO branding
const dashboardTheme = createTheme(theme, {
  components: {
    ...theme.components,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#206884', // IMO Teal - exact match to website
          color: '#ffffff',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          height: '100%',
        },
        paper: {
          backgroundColor: '#ffffff',
          color: theme.palette.text.primary,
          width: 240,
          height: '100%',
          top: 0,
          bottom: 0,
          borderRight: `1px solid ${theme.palette.grey[200]}`,
        },
      },
    },
  },
});

// Removed mock useAuth - now using the real AuthContext

const DashboardLayout = ({ children, title = 'Dashboard', breadcrumbData = {} }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [accountTypeMenuAnchor, setAccountTypeMenuAnchor] = useState(null);
  const [stripeConnectAccount, setStripeConnectAccount] = useState(null);
  const [isStripeConnectValid, setIsStripeConnectValid] = useState(true);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [hasBranch, setHasBranch] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [isCheckingSetup, setIsCheckingSetup] = useState(true);
  const [setupStep, setSetupStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [numberOfBranches, setNumberOfBranches] = useState(0);
  const [showStripeConnectModal, setShowStripeConnectModal] = useState(false);
  const [stripeOnboardingComplete, setStripeOnboardingComplete] = useState(false);
  const [branchFormData, setBranchFormData] = useState({
    branchName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    contactPersonName: '',
  });
  const [branchErrors, setBranchErrors] = useState({});
  const [isCreatingBranch, setIsCreatingBranch] = useState(false);
  const [existingBranch, setExistingBranch] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    currentAccountType,
    availableAccountTypes,
    switchAccountType,
    logout,
    getPaymentMethods,
    registrationPassare,
    clearRegistrationPassare,
  } = useAuth();
  const [selectedPassareLocations, setSelectedPassareLocations] = useState({});
  const [isSavingPassareLocations, setIsSavingPassareLocations] = useState(false);
  const [selectedPassareOrganizationUuid, setSelectedPassareOrganizationUuid] = useState(null);
  const [passareOrganizationConfirmed, setPassareOrganizationConfirmed] = useState(false);
  const [linkedCompanyPassare, setLinkedCompanyPassare] = useState(null);
  const passareSetupData = registrationPassare || linkedCompanyPassare;
  const passareOrganizations = useMemo(() => {
    if (!passareSetupData) return [];

    const organizations = Array.isArray(passareSetupData.organizations) && passareSetupData.organizations.length > 0
      ? passareSetupData.organizations
      : [{
        organizationUuid: passareSetupData.organizationUuid,
        organizationName: passareSetupData.organizationName,
        branches: passareSetupData.branches || [],
      }];

    return organizations
      .map((organization, index) => normalizePassareOrganization(organization, index))
      .filter((organization) => organization.branches.length > 0);
  }, [passareSetupData]);
  const selectedPassareOrganization = useMemo(
    () =>
      passareOrganizations.find(
        (organization) => organization.organizationUuid === selectedPassareOrganizationUuid,
      ) || null,
    [passareOrganizations, selectedPassareOrganizationUuid],
  );
  const passareLocations = useMemo(
    () => selectedPassareOrganization?.branches || [],
    [selectedPassareOrganization],
  );
  const requiresPassareChoice = passareOrganizations.length > 1 && !passareOrganizationConfirmed;
  const hasPassareHit = Boolean(
    selectedPassareOrganization &&
    passareLocations.length > 0 &&
    !requiresPassareChoice,
  );
  const selectedPassareCount = passareLocations.reduce(
    (count, branch) => count + ((selectedPassareLocations[branch.branchUuid] ?? true) ? 1 : 0),
    0,
  );
  
  // Generate breadcrumbs
  const { breadcrumbs } = useBreadcrumbs(breadcrumbData);

  useEffect(() => {
    checkAccountSetup();
  }, [currentAccountType, user]);

  useEffect(() => {
    if (passareOrganizations.length === 0) {
      setSelectedPassareOrganizationUuid(null);
      setPassareOrganizationConfirmed(false);
      return;
    }

    if (passareOrganizations.length === 1) {
      setSelectedPassareOrganizationUuid(passareOrganizations[0].organizationUuid);
      setPassareOrganizationConfirmed(true);
      return;
    }

    setSelectedPassareOrganizationUuid((previousUuid) => {
      if (previousUuid && passareOrganizations.some((organization) => organization.organizationUuid === previousUuid)) {
        return previousUuid;
      }
      return passareOrganizations[0].organizationUuid;
    });
    setPassareOrganizationConfirmed(false);
  }, [passareOrganizations]);

  useEffect(() => {
    if (!hasPassareHit) {
      setSelectedPassareLocations({});
      return;
    }

    setSelectedPassareLocations((previous) => {
      const nextSelection = {};
      passareLocations.forEach((passareLocation) => {
        nextSelection[passareLocation.branchUuid] = previous[passareLocation.branchUuid] ?? true;
      });
      return nextSelection;
    });
  }, [hasPassareHit, passareLocations]);

  useEffect(() => {
    // Hard gate: no locations means payment/plan steps are not reachable.
    if (!hasBranch && setupStep > 0) {
      setSetupStep(0);
    }
  }, [hasBranch, setupStep]);

  const checkAccountSetup = async () => {
    try {
      // Account setup modal is only required for funeral directors
      // Admins can skip this modal even when viewing as funeral director
      // Designers can skip this modal
      if (currentAccountType !== 'funeral_director' || user?.type === 'admin') {
        setSetupComplete(true);
        setIsCheckingSetup(false);
        return;
      }

      // Check payment methods (Step 1)
      const paymentMethods = await getPaymentMethods();
      const hasPayment = paymentMethods && paymentMethods.length > 0;
      setHasPaymentMethod(hasPayment);
      setPaymentMethods(paymentMethods || []);
      
      // Set the first payment method as selected by default
      if (hasPayment) {
        setSelectedPaymentMethodId(paymentMethods[0].id);
      }
      
      // Show form if no payment method exists
      if (!hasPayment) {
        setShowAddPaymentForm(true);
      }

      // Check if user has a branch (for branch form prepopulation only)
      let hasBranchSetup = false;
      let existingBranchData = null;
      try {
        const branches = await fetchAllCompanyBranches(user?.companyId);
        
        const activeBranchCount = getActiveBranchCount(branches);
        hasBranchSetup = activeBranchCount > 0;
        setHasBranch(hasBranchSetup);
        setNumberOfBranches(hasBranchSetup ? activeBranchCount : 0);

        // If branch exists, store it and load its data into the form
        if (hasBranchSetup && branches[0]) {
          existingBranchData = branches[0];
          setExistingBranch(existingBranchData);
          
          // Prepopulate form with existing branch data
          setBranchFormData({
            branchName: existingBranchData.branchName || '',
            address: existingBranchData.address || '',
            city: existingBranchData.city || '',
            state: existingBranchData.state || '',
            zipCode: existingBranchData.zipCode || '',
            phone: existingBranchData.phone || '',
            email: existingBranchData.email || '',
            contactPersonName: existingBranchData.contactPersonName || '',
          });
        }
        } catch (error) {
          setHasBranch(false);
          setNumberOfBranches(0);
          setExistingBranch(null);
        }

      // Check subscription on company (subscriptions are now attached to company, not branch)
      let hasSubscription = false;
      if (user?.companyId) {
        try {
          const companyResponse = await apiClient.getFuneralCompany(user.companyId);
          const company = companyResponse.company || companyResponse.data || companyResponse;

          if (!hasBranchSetup && !registrationPassare && company?.passareOrgUuid) {
            try {
              const linkedPassareResponse = await apiClient.getCompanyPassareSetup(user.companyId);
              const linkedPassare = linkedPassareResponse?.passare || linkedPassareResponse?.data || null;
              setLinkedCompanyPassare(linkedPassare?.branches?.length ? linkedPassare : null);
            } catch {
              setLinkedCompanyPassare(null);
            }
          } else {
            setLinkedCompanyPassare(null);
          }
          
          // Check if company has a subscription
          hasSubscription = !!(company.subscriptionId || company.subscriptionPlan);
          
          // If company has a subscription plan, set it in state
          if (company.subscriptionPlan) {
            setSelectedPlan(company.subscriptionPlan);
          }
          
          // Prepopulate branch form with company and user information (only if no existing branch)
          if (!hasBranchSetup) {
            // Try to get address from Stripe Connect account if available
            let addressData = {};
            if (company.stripeConnectAccount) {
              try {
                const stripeAccountResponse = await apiClient.getStripeConnectAccount(company.stripeConnectAccount);
                const stripeAccount = stripeAccountResponse.data || stripeAccountResponse;
                
                if (stripeAccount.company?.address) {
                  addressData = {
                    address: stripeAccount.company.address.line1 || '',
                    city: stripeAccount.company.address.city || '',
                    state: stripeAccount.company.address.state || '',
                    zipCode: stripeAccount.company.address.postal_code || '',
                  };
                } else if (stripeAccount.individual?.address) {
                  addressData = {
                    address: stripeAccount.individual.address.line1 || '',
                    city: stripeAccount.individual.address.city || '',
                    state: stripeAccount.individual.address.state || '',
                    zipCode: stripeAccount.individual.address.postal_code || '',
                  };
                }
              } catch (stripeError) {
                // Error loading Stripe address - continue without it
              }
            }
            
            setBranchFormData({
              branchName: company.companyName || '',
              address: addressData.address || '',
              city: addressData.city || '',
              state: addressData.state || '',
              zipCode: addressData.zipCode || '',
              phone: company.phone || '',
              email: user?.email || '',
              contactPersonName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
            });
          }
        } catch (error) {
          // If company fetch fails, at least prepopulate with user info
          if (!hasBranchSetup) {
            setBranchFormData(prev => ({
              ...prev,
              email: user?.email || '',
              contactPersonName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
            }));
          }
        }
      }

      // Check Stripe Connect account (not required for setup, skip for staff users)
      if (currentAccountType !== 'staff') {
        const stripeConnectResponse = await apiClient.getMyStripeConnectAccount();
        setStripeConnectAccount(stripeConnectResponse.data);
        
        // Check if Stripe Connect account is valid using comprehensive verification check
        // Account is verified if: details_submitted, charges_enabled, payouts_enabled are all true,
        // and there are no past_due requirements
        const accountData = stripeConnectResponse.data;
        const isStripeConnectValid = stripeConnectResponse.success && 
          stripeConnectResponse.hasAccountId && 
          accountData && 
          accountData.details_submitted === true &&
          accountData.charges_enabled === true &&
          accountData.payouts_enabled === true &&
          (!accountData.requirements?.past_due || accountData.requirements.past_due.length === 0);
        
        setIsStripeConnectValid(isStripeConnectValid);
      }

      // Check if all steps are complete: locations + payment + subscription
      const isSetupComplete = hasBranchSetup && hasPayment && hasSubscription;
      setSetupComplete(isSetupComplete);

      // Set initial step based on what's missing
      if (!hasBranchSetup) {
        setSetupStep(0); // Step 1: Locations
      } else if (!hasPayment) {
        setSetupStep(1); // Step 2: Payment method
      } else if (!hasSubscription) {
        setSetupStep(2); // Step 3: Select plan
      }

      setIsCheckingSetup(false);
    } catch (error) {
      setIsCheckingSetup(false);
    }
  };

  const handlePaymentMethodComplete = async () => {
    // Recheck payment method status
    const paymentMethods = await getPaymentMethods();
    const hasPayment = paymentMethods && paymentMethods.length > 0;
    setHasPaymentMethod(hasPayment);
    setPaymentMethods(paymentMethods || []);
    
    // Set the first payment method as selected
    if (hasPayment) {
      setSelectedPaymentMethodId(paymentMethods[0].id);
    }
    
    // Hide the add payment form and show the list
    setShowAddPaymentForm(false);
    
    // Move to step 3: Select plan (locations are completed before payment)
    setSetupStep(hasBranch ? 2 : 0);
  };

  const handleBranchCreated = async () => {
    // Validate branch form
    const newErrors = {};
    if (!branchFormData.branchName.trim()) {
      newErrors.branchName = 'Branch name is required';
    }
    if (!branchFormData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!branchFormData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!branchFormData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!branchFormData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setBranchErrors(newErrors);
      return;
    }

    setBranchErrors({});
    setIsCreatingBranch(true);

    try {
      // Prepare submit data
      const submitData = {
        branchName: branchFormData.branchName,
        address: branchFormData.address,
        city: branchFormData.city,
        state: branchFormData.state,
        zipCode: branchFormData.zipCode,
      };

      // Only add optional fields if they have values
      if (branchFormData.contactPersonName.trim()) {
        submitData.contactPersonName = branchFormData.contactPersonName;
      }
      if (branchFormData.phone.trim()) {
        submitData.phone = branchFormData.phone;
      }
      if (branchFormData.email.trim()) {
        submitData.email = branchFormData.email;
      }

      if (existingBranch) {
        // Update existing branch
        const updatedBranch = await apiClient.updateBranch(existingBranch.id, submitData);
        
        // Extract the branch data from the response
        const branchData = updatedBranch.branch || updatedBranch.data || updatedBranch;
        setExistingBranch(branchData);
      } else {
        // Create new branch - ensure user has a companyId
        if (!user?.companyId) {
          setBranchErrors({ general: 'User is not associated with a company' });
          setIsCreatingBranch(false);
          return;
        }

        submitData.companyId = user.companyId;
        const createdBranch = await apiClient.createBranch(submitData);
        
        // Extract the branch data from the response
        const branchData = createdBranch.branch || createdBranch.data || createdBranch;
        setExistingBranch(branchData);
      }

      const branches = await fetchAllCompanyBranches(user.companyId);
      const activeBranchCount = getActiveBranchCount(branches);
      applyLocationsGate(activeBranchCount);

      setIsCreatingBranch(false);
    } catch (error) {
      const errorMessage = error.response?.data?.message || (existingBranch ? 'Failed to update branch' : 'Failed to create branch');
      setBranchErrors({ general: errorMessage });
      setIsCreatingBranch(false);
    }
  };

  const handlePlanSelected = (plan) => {
    setSelectedPlan(plan);
    // Don't auto-complete, wait for user to click "Finish"
  };

  const handleFinishSetup = async () => {
    if (!hasBranch || !selectedPlan || !selectedPaymentMethodId || !numberOfBranches || numberOfBranches < 1) {
      alert('Please ensure you have at least one location, a payment method, and a selected plan');
      return;
    }

    setIsCreatingBranch(true); // Reuse loading state

    try {
      // Create Stripe subscription attached to company
      const subscriptionData = {
        plan: selectedPlan, // 'monthly' or 'annual'
        paymentMethodId: selectedPaymentMethodId,
        numberOfBranches: numberOfBranches,
      };

      const subscription = await apiClient.createSubscription(subscriptionData);

      // All steps complete - mark setup as complete and navigate to branches page
      setSetupComplete(true);
      navigate('/branches');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create subscription';
      // Show error in a toast or alert
      alert(errorMessage);
    } finally {
      setIsCreatingBranch(false);
    }
  };

  const handleBackToStep = (step) => {
    if (!hasBranch && step > 0) {
      setSetupStep(0);
      return;
    }
    setSetupStep(step);
  };

  const applyLocationsGate = (activeBranchCount) => {
    const hasAtLeastOneLocation = activeBranchCount > 0;
    setHasBranch(hasAtLeastOneLocation);
    setNumberOfBranches(hasAtLeastOneLocation ? activeBranchCount : 0);

    if (hasAtLeastOneLocation) {
      setSetupStep(hasPaymentMethod ? 2 : 1);
    }
  };

  const fetchAllCompanyBranches = async (companyId) => {
    if (!companyId) {
      return [];
    }

    const allBranches = [];
    let page = 1;

    while (page <= 500) {
      const response = await apiClient.getBranches({ companyId, page, limit: BRANCHES_PAGE_LIMIT });
      const branches = Array.isArray(response?.data) ? response.data : [];
      allBranches.push(...branches);

      const pagination = response?.pagination || {};
      const hasNext = pagination.hasNext ?? pagination.has_next;
      const totalPagesRaw = pagination.totalPages ?? pagination.total_pages ?? pagination.pages;
      const totalRaw = pagination.total ?? pagination.count;
      const totalPages = Number(totalPagesRaw);
      const total = Number(totalRaw);

      if (hasNext === true) {
        page += 1;
        continue;
      }

      if (Number.isFinite(totalPages) && totalPages > 0) {
        if (page >= totalPages) {
          break;
        }
        page += 1;
        continue;
      }

      if (Number.isFinite(total) && total > 0) {
        if (allBranches.length >= total) {
          break;
        }
        page += 1;
        continue;
      }

      if (branches.length < BRANCHES_PAGE_LIMIT) {
        break;
      }

      page += 1;
    }

    return allBranches;
  };

  const handlePassareLocationToggle = (branchUuid) => (event) => {
    setSelectedPassareLocations((previous) => ({
      ...previous,
      [branchUuid]: event.target.checked,
    }));
  };

  const handlePassareOrganizationChange = (event) => {
    setSelectedPassareOrganizationUuid(event.target.value);
    setPassareOrganizationConfirmed(false);
  };

  const handleContinueWithPassareOrganization = () => {
    if (!selectedPassareOrganizationUuid) return;
    setPassareOrganizationConfirmed(true);
  };

  const buildPassareBranchPayload = (passareLocation, isActive) => ({
    branchName: passareLocation.branchName,
    address: passareLocation.addressLine1 || '',
    city: passareLocation.city || '',
    state: passareLocation.state || '',
    zipCode: passareLocation.zipCode || '',
    passareBranchUuid: passareLocation.branchUuid,
    active: isActive,
    isActive: isActive,
  });

  const savePassareLocations = async () => {
    if (!user?.companyId) {
      setBranchErrors({ general: 'User is not associated with a company' });
      return;
    }

    setBranchErrors({});
    setIsSavingPassareLocations(true);

    try {
      const existingBranches = await fetchAllCompanyBranches(user.companyId);
      const existingByPassareUuid = new Map(
        existingBranches
          .filter((branch) => branch.passareBranchUuid)
          .map((branch) => [branch.passareBranchUuid, branch]),
      );

      for (const passareLocation of passareLocations) {
        const isActive = selectedPassareLocations[passareLocation.branchUuid] ?? true;
        const payload = buildPassareBranchPayload(passareLocation, isActive);
        const existingBranchForLocation = existingByPassareUuid.get(passareLocation.branchUuid);

        if (existingBranchForLocation?.id) {
          await apiClient.updateBranch(existingBranchForLocation.id, payload);
          continue;
        }

        await apiClient.createBranch({
          ...payload,
          companyId: user.companyId,
        });
      }

      const refreshedBranches = await fetchAllCompanyBranches(user.companyId);
      const activeBranchCount = getActiveBranchCount(refreshedBranches);
      applyLocationsGate(activeBranchCount);

      clearRegistrationPassare();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save locations';
      setBranchErrors({ general: errorMessage });
    } finally {
      setIsSavingPassareLocations(false);
    }
  };

  const handleStripeConnectComplete = async () => {
    // Mark onboarding as complete so the continue button shows
    setStripeOnboardingComplete(true);
  };

  const handleContinueToDashboard = async () => {
    // Refresh Stripe Connect status
    const stripeConnectResponse = await apiClient.getMyStripeConnectAccount();
    setStripeConnectAccount(stripeConnectResponse.data);
    // Close the modal
    setShowStripeConnectModal(false);
    setStripeOnboardingComplete(false); // Reset for next time
  };

  const handleCloseStripeConnectModal = () => {
    // User can set up Stripe Connect later from My Account
    setShowStripeConnectModal(false);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };


  const handleAccountMenuOpen = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleAccountNavigation = (path) => {
    navigate(path);
    handleAccountMenuClose();
  };

  const handleLogout = () => {
    logout();
    handleAccountMenuClose();
    navigate('/login');
  };

  const handleAccountTypeMenuOpen = (event) => {
    setAccountTypeMenuAnchor(event.currentTarget);
  };

  const handleAccountTypeMenuClose = () => {
    setAccountTypeMenuAnchor(null);
  };

  const handleAccountTypeSwitch = (accountType) => {
    switchAccountType(accountType);
    handleAccountTypeMenuClose();
  };

  const getAccountTypeIcon = (type) => {
    switch (type) {
      case 'admin':
        return <AdminIcon />;
      case 'funeral_director':
        return <BusinessIcon />;
      case 'designer':
        return <DesignIcon />;
      case 'staff':
        return <GroupsIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getAccountTypeLabel = (type) => {
    switch (type) {
      case 'admin':
        return 'Admin';
      case 'funeral_director':
        return 'Funeral Director';
      case 'designer':
        return 'Designer';
      case 'staff':
        return 'Staff';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getNavigationItems = () => {
    const navItems = [];

    switch (currentAccountType) {
      case 'admin':
        navItems.push(...[
          {
            text: 'Dashboard',
            icon: <DashboardIcon />,
            path: '/',
          },
          {
            text: 'Users',
            icon: <PeopleIcon />,
            path: '/users',
          },
          {
            text: 'Cards',
            icon: <CardIcon />,
            path: '/cards',
          },
          {
            text: 'Companies',
            icon: <BusinessIcon />,
            path: '/companies',
          },
          {
            text: 'Designers',
            icon: <PaletteIcon />,
            path: '/designers',
          },
          {
            text: 'Obituaries',
            icon: <DescriptionIcon />,
            path: '/obituaries',
          },
          {
            text: 'Admin Tools',
            icon: <ToolsIcon />,
            path: '/admin-tools',
          },
        ]);
        break;
      case 'funeral_director':
        navItems.push(...[
          {
            text: 'Dashboard',
            icon: <DashboardIcon />,
            path: '/',
          },
          {
            text: 'Users',
            icon: <PeopleIcon />,
            path: '/users',
          },
          {
            text: 'Branches',
            icon: <BusinessIcon />,
            path: '/branches',
          },
          {
            text: 'Obituaries',
            icon: <DescriptionIcon />,
            path: '/obituaries',
          },
        ]);
        break;
      case 'designer':
        navItems.push(...[
          {
            text: 'Dashboard',
            icon: <DashboardIcon />,
            path: '/',
          },
          {
            text: 'My Cards',
            icon: <CardIcon />,
            path: '/cards',
          },
        ]);
        break;
      case 'staff':
        navItems.push(...[
          {
            text: 'Dashboard',
            icon: <DashboardIcon />,
            path: '/',
          },
          {
            text: 'Obituaries',
            icon: <DescriptionIcon />,
            path: '/obituaries',
          },
        ]);
        break;
    }

    return navItems;
  };
  
  // Mobile drawer - always full expanded view
  const mobileDrawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <List sx={{ pt: 1, flex: 1 }}>
        {getNavigationItems().map((item) => (
          <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  minHeight: 48,
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  py: 1.5,
                  color: theme.palette.primary.main,
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: 'rgba(32, 104, 132, 0.04)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(32, 104, 132, 0.04)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                  color: theme.palette.primary.main,
                  minWidth: 40,
                  mr: 1,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  '& .MuiTypography-root': {
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                    color: theme.palette.primary.main,
                  }
                }}
              />
              </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Desktop drawer - responsive to sidebarCollapsed state
  const desktopDrawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <List sx={{ pt: 1, flex: 1 }}>
        {getNavigationItems().map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                mx: sidebarCollapsed ? 0.5 : 1,
                borderRadius: 1,
                minHeight: 48,
                flexDirection: sidebarCollapsed ? 'column' : 'row',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                alignItems: sidebarCollapsed ? 'center' : 'flex-start',
                py: sidebarCollapsed ? 1 : 1.5,
                color: '#2c4575',
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  color: '#2c4575',
                  '&:hover': {
                    backgroundColor: 'rgba(44, 69, 117, 0.04)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(44, 69, 117, 0.04)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: theme.palette.primary.main,
                  minWidth: sidebarCollapsed ? 0 : 40,
                  mr: sidebarCollapsed ? 0 : 1,
                  mb: sidebarCollapsed ? 0.5 : 0,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{
                  display: 'block',
                  textAlign: sidebarCollapsed ? 'center' : 'left',
                  '& .MuiTypography-root': {
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    fontSize: sidebarCollapsed ? '0.75rem' : '0.875rem',
                    lineHeight: 1.2,
                    color: theme.palette.primary.main,
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={dashboardTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* App Bar - Full width */}
        <AppBar
          position="static"
          sx={{
            backgroundColor: '#206884 !important', // IMO Teal - exact match to website
            color: '#ffffff !important',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Toolbar>
            {/* Mobile menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' }, color: 'white' }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Desktop sidebar toggle button */}
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              edge="start"
              onClick={handleSidebarToggle}
              sx={{ mr: 2, display: { xs: 'none', md: 'block' }, color: 'white' }}
            >
              {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <div className="logo-group" style={{
                position: 'relative',
                backgroundColor: '#6CCC16',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                borderBottomLeftRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                marginRight: '12px',
              }}>
                <div style={{
                  overflow: 'hidden',
                  transition: 'all 0.5s ease-in-out',
                  maxWidth: '36px',
                }} className="logo-text-container">
                  <span style={{
                    fontWeight: 900,
                    fontSize: '18px',
                    letterSpacing: '-0.5px',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}>IMObituary</span>
                </div>
                <div style={{
                  position: 'relative',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'visible',
                  width: '18px',
                  height: '18px',
                }}>
                  {/* Mail truck */}
                  <div className="mail-truck" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    opacity: 0,
                  }}>
                    <i className="fa-solid fa-truck" style={{ fontSize: '10px', color: 'white' }}></i>
                  </div>
                  {/* Envelope icons */}
                  <i className="fa-regular fa-envelope envelope-closed-animate" style={{
                    fontSize: '14px',
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20,
                    opacity: 1,
                  }}></i>
                  <i className="fa-regular fa-envelope-open envelope-open-animate" style={{
                    fontSize: '14px',
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20,
                    opacity: 0,
                  }}></i>
                  {/* Dollar signs */}
                  <span className="dollar-sign-1" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '11px',
                    opacity: 0,
                  }}>$</span>
                  <span className="dollar-sign-2" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '11px',
                    opacity: 0,
                  }}>$</span>
                  <span className="dollar-sign-3" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '11px',
                    opacity: 0,
                  }}>$</span>
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '-6px',
                  left: 0,
                  width: 0,
                  height: 0,
                  borderTop: '6px solid #6CCC16',
                  borderRight: '6px solid transparent',
                }}></div>
              </div>
            </Box>
            
            {/* Current Account Type Indicator */}
            {currentAccountType && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2, color: 'white' }}>
                {getAccountTypeIcon(currentAccountType)}
                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.875rem' }}>
                  {getAccountTypeLabel(currentAccountType)}
                </Typography>
              </Box>
            )}
            
            {/* Right side actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Account Type Switcher - Only show if logged-in user is an admin */}
              {user?.type === 'admin' && (
                <IconButton 
                  color="inherit" 
                  onClick={handleAccountTypeMenuOpen}
                  sx={{ color: 'white' }}
                  aria-label="switch account type"
                  aria-controls="account-type-menu"
                  aria-haspopup="true"
                  title={`Available types: ${availableAccountTypes.join(', ')}`}
                >
                  <SwapIcon />
                </IconButton>
              )}
              
              <IconButton color="inherit" sx={{ color: 'white' }}>
                <NotificationsIcon />
              </IconButton>
              <IconButton 
                color="inherit" 
                onClick={handleAccountMenuOpen}
                sx={{ color: 'white' }}
                aria-label="account menu"
                aria-controls="account-menu"
                aria-haspopup="true"
              >
                <AccountCircleIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Account Type Menu Dropdown */}
        <Menu
          id="account-type-menu"
          anchorEl={accountTypeMenuAnchor}
          open={Boolean(accountTypeMenuAnchor)}
          onClose={handleAccountTypeMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 200,
              mt: 1,
              '& .MuiMenuItem-root': {
                py: 1.5,
                px: 2,
              },
            },
          }}
        >
          <MenuItem disabled>
            <ListItemIcon>
              <SwapIcon fontSize="small" />
            </ListItemIcon>
            Switch Account Type
          </MenuItem>
          <Divider />
          {availableAccountTypes.length > 0 ? (
            availableAccountTypes.map((accountType) => (
              <MenuItem 
                key={accountType}
                onClick={() => handleAccountTypeSwitch(accountType)}
                selected={currentAccountType === accountType}
              >
                <ListItemIcon>
                  {getAccountTypeIcon(accountType)}
                </ListItemIcon>
                {getAccountTypeLabel(accountType)}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              No other account types available
            </MenuItem>
          )}
        </Menu>

        {/* Account Menu Dropdown */}
        <Menu
          id="account-menu"
          anchorEl={accountMenuAnchor}
          open={Boolean(accountMenuAnchor)}
          onClose={handleAccountMenuClose}
          onClick={handleAccountMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 200,
              mt: 1,
              '& .MuiMenuItem-root': {
                py: 1.5,
                px: 2,
              },
            },
          }}
        >
          <MenuItem onClick={() => handleAccountNavigation('/my-account')}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            My Account
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>

        {/* Main content area with sidebar and content */}
        <Box sx={{ display: 'flex', flex: 1, height: 'calc(100vh - 64px)' }}>
          {/* Sidebar */}
          <Box
            component="nav"
            sx={{ 
              width: { md: sidebarCollapsed ? 64 : 240 }, 
              flexShrink: { md: 0 },
              minHeight: 'calc(100vh - 64px)', // Subtract AppBar height
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            }}
          >
            {/* Mobile drawer */}
            <Drawer
              variant="temporary"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-root': {
                  height: '100%',
                },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: 240,
                  top: 0,
                  bottom: 0,
                  backgroundColor: '#ffffff',
                  borderRight: `1px solid ${theme.palette.grey[200]}`,
                },
              }}
            >
              {mobileDrawer}
            </Drawer>
            
            {/* Desktop drawer */}
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-root': {
                  height: '100%',
                },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: sidebarCollapsed ? 64 : 240,
                  transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.easeInOut,
                    duration: theme.transitions.duration.standard,
                  }),
                  overflowX: 'hidden',
                  position: 'relative',
                  top: 0,
                  bottom: 0,
                  backgroundColor: '#f7f7f7',
                  borderRight: '1px solid #e0e0e0',
                },
              }}
              open
            >
              {desktopDrawer}
            </Drawer>
          </Box>

          {/* Main content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              pt: 3,
              minHeight: 'calc(100vh - 64px)', // Subtract AppBar height
              width: { 
                xs: '100%', // Full width on mobile
                md: `calc(100% - ${sidebarCollapsed ? 64 : 240}px)` // Responsive width on desktop
              },
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            }}
          >
            <Container maxWidth="xl">
              {/* Breadcrumbs */}
              <Breadcrumbs items={breadcrumbs} />
              {children}
            </Container>
          </Box>
        </Box>

            {/* Subscription Setup Modal */}
            <Dialog 
              open={!setupComplete && !isCheckingSetup}
              maxWidth="md"
              fullWidth
              disableEscapeKeyDown
              scroll="paper"
              sx={{
                '& .MuiDialog-paper': {
                  borderRadius: 2,
                  maxHeight: 'calc(100vh - 24px)',
                  height: 'min(760px, calc(100vh - 24px))',
                  overflow: 'hidden',
                  display: 'flex',
                }
              }}
            >
          <DialogTitle sx={{ 
            textAlign: 'center', 
            pt: 4,
            pb: 2,
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2 
            }}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <WarningIcon sx={{ fontSize: 48, color: '#f59e0b' }} />
              </Box>
            </Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  Account Setup Required
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mt: 1 }}
                >
                  Complete these steps to access your dashboard
                </Typography>
          </DialogTitle>
          
          <DialogContent
            data-testid="account-setup-dialog-content"
            sx={{ px: 4, pb: 4, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
          >
            {/* Stepper */}
            <Box sx={{ mb: 4, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {/* Step 1 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <Box sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: setupStep >= 0 ? theme.palette.primary.main : '#e0e0e0',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    marginBottom: '8px'
                  }}>
                    {hasBranch ? '✓' : '1'}
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: '12px',
                    color: setupStep >= 0 ? theme.palette.primary.main : '#999',
                    fontWeight: setupStep === 0 ? '600' : '400',
                    textAlign: 'center'
                  }}>
                    Locations
                  </Typography>
                </Box>

                {/* Connector Line */}
                <Box sx={{
                  height: '2px',
                  flex: 1,
                  backgroundColor: hasBranch ? theme.palette.primary.main : '#e0e0e0',
                  margin: '0 8px',
                  marginBottom: '28px'
                }} />

                {/* Step 2 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <Box sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: setupStep >= 1 ? theme.palette.primary.main : '#e0e0e0',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    marginBottom: '8px'
                  }}>
                    {hasPaymentMethod ? '✓' : '2'}
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: '12px',
                    color: setupStep >= 1 ? theme.palette.primary.main : '#999',
                    fontWeight: setupStep === 1 ? '600' : '400',
                    textAlign: 'center'
                  }}>
                    Payment Method
                  </Typography>
                </Box>

                {/* Connector Line */}
                <Box sx={{
                  height: '2px',
                  flex: 1,
                  backgroundColor: hasPaymentMethod ? theme.palette.primary.main : '#e0e0e0',
                  margin: '0 8px',
                  marginBottom: '28px'
                }} />

                {/* Step 3 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <Box sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: setupStep >= 2 ? theme.palette.primary.main : '#e0e0e0',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    marginBottom: '8px'
                  }}>
                    {selectedPlan ? '✓' : '3'}
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: '12px',
                    color: setupStep >= 2 ? theme.palette.primary.main : '#999',
                    fontWeight: setupStep === 2 ? '600' : '400',
                    textAlign: 'center'
                  }}>
                    Select Plan
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Step 1: Locations */}
            {setupStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, flexShrink: 0 }}>
                  {!hasPassareHit && !hasBranch && !requiresPassareChoice ? 'Add Your Locations' : 'Step 1: Locations'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexShrink: 0 }}>
                  Add at least one location to continue. Your subscription pricing is calculated from saved locations.
                </Typography>

                {branchErrors.general && (
                  <Typography color="error" variant="body2" sx={{ mb: 2, flexShrink: 0 }}>
                    {branchErrors.general}
                  </Typography>
                )}

                {requiresPassareChoice ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.success.light}`,
                        backgroundColor: '#ecfdf5',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#166534' }}>
                        Choose your Passare account
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        We found multiple organizations for your email. Choose one to continue with location setup.
                      </Typography>
                    </Box>

                    <Box
                      data-testid="passare-organization-choices"
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        overflowY: 'auto',
                        flex: 1,
                        minHeight: 0,
                        backgroundColor: '#fff',
                      }}
                    >
                      <RadioGroup
                        value={selectedPassareOrganizationUuid || ''}
                        onChange={handlePassareOrganizationChange}
                      >
                        {passareOrganizations.map((organization) => (
                          <Box
                            key={organization.organizationUuid}
                            sx={{
                              border: '1px solid #e5e7eb',
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.75,
                              mb: 1.5,
                            }}
                          >
                            <FormControlLabel
                              value={organization.organizationUuid}
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1f2937' }}>
                                    {organization.organizationName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {organization.branches.length} {organization.branches.length === 1 ? 'location' : 'locations'}
                                  </Typography>
                                </Box>
                              }
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleContinueWithPassareOrganization}
                      disabled={!selectedPassareOrganizationUuid}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#999',
                        },
                      }}
                    >
                      Continue with selected account
                    </Button>
                  </Box>
                ) : hasPassareHit ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.success.light}`,
                        backgroundColor: '#ecfdf5',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#166534' }}>
                        Passare account found: {selectedPassareOrganization.organizationName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Every location will be linked. Uncheck a location only if it should be inactive and not billed.
                      </Typography>
                    </Box>

                    <Box
                      data-testid="passare-locations-scroll"
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        pr: 1,
                        mb: 2,
                      }}
                    >
                      {passareLocations.map((passareLocation) => (
                        <Box
                          key={passareLocation.branchUuid}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            border: '1px solid #e5e7eb',
                            borderRadius: 2,
                            p: 1.5,
                            mb: 1.5,
                            backgroundColor: '#fff',
                          }}
                        >
                          <Checkbox
                            checked={selectedPassareLocations[passareLocation.branchUuid] ?? true}
                            onChange={handlePassareLocationToggle(passareLocation.branchUuid)}
                            inputProps={{ 'aria-label': `Set ${passareLocation.branchName} as active` }}
                            sx={{ mt: -0.25 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {passareLocation.branchName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {passareLocation.addressLabel}
                            </Typography>
                            {!passareLocation.hasAddress && (
                              <Typography variant="caption" sx={{ color: '#92400e', display: 'block', mt: 0.5 }}>
                                No address was provided by Passare.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    <Box
                      data-testid="passare-locations-actions"
                      sx={{
                        borderTop: '1px solid #e5e7eb',
                        pt: 2,
                        flexShrink: 0,
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: '#fff',
                      }}
                    >
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={savePassareLocations}
                        disabled={isSavingPassareLocations || selectedPassareCount === 0}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          '&:hover': { backgroundColor: theme.palette.primary.dark },
                          '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#999',
                          },
                        }}
                      >
                        {isSavingPassareLocations
                          ? 'Saving Locations...'
                          : `Link ${passareLocations.length} locations (${selectedPassareCount} active)`}
                      </Button>
                    </Box>
                  </Box>
                ) : hasBranch ? (
                  <Box>
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.success.light}`,
                        backgroundColor: '#ecfdf5',
                        borderRadius: 2,
                        p: 2,
                        mb: 3,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#166534' }}>
                        {numberOfBranches} {numberOfBranches === 1 ? 'location is' : 'locations are'} saved
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Continue to payment once your locations look correct.
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => setSetupStep(1)}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      }}
                    >
                      Next: Payment Method
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                      <TextField
                        label="Branch Name"
                        value={branchFormData.branchName}
                        onChange={(event) => setBranchFormData({ ...branchFormData, branchName: event.target.value })}
                        error={!!branchErrors.branchName}
                        helperText={branchErrors.branchName || ''}
                        required
                        fullWidth
                      />
                      <TextField
                        label="Street Address"
                        value={branchFormData.address}
                        onChange={(event) => setBranchFormData({ ...branchFormData, address: event.target.value })}
                        error={!!branchErrors.address}
                        helperText={branchErrors.address || ''}
                        required
                        fullWidth
                      />
                      <TextField
                        label="City"
                        value={branchFormData.city}
                        onChange={(event) => setBranchFormData({ ...branchFormData, city: event.target.value })}
                        error={!!branchErrors.city}
                        helperText={branchErrors.city || ''}
                        required
                        fullWidth
                      />
                      <TextField
                        label="State"
                        value={branchFormData.state}
                        onChange={(event) => setBranchFormData({ ...branchFormData, state: event.target.value })}
                        error={!!branchErrors.state}
                        helperText={branchErrors.state || ''}
                        required
                        fullWidth
                      />
                      <TextField
                        label="ZIP Code"
                        value={branchFormData.zipCode}
                        onChange={(event) => setBranchFormData({ ...branchFormData, zipCode: event.target.value })}
                        error={!!branchErrors.zipCode}
                        helperText={branchErrors.zipCode || ''}
                        required
                        fullWidth
                      />
                      <TextField
                        label="Contact Person Name"
                        value={branchFormData.contactPersonName}
                        onChange={(event) => setBranchFormData({ ...branchFormData, contactPersonName: event.target.value })}
                        fullWidth
                      />
                      <TextField
                        label="Phone Number"
                        value={branchFormData.phone}
                        onChange={(event) => setBranchFormData({ ...branchFormData, phone: event.target.value })}
                        fullWidth
                      />
                      <TextField
                        label="Email Address"
                        value={branchFormData.email}
                        onChange={(event) => setBranchFormData({ ...branchFormData, email: event.target.value })}
                        fullWidth
                      />
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleBranchCreated}
                      disabled={isCreatingBranch}
                      sx={{
                        mt: 3,
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#999',
                        },
                      }}
                    >
                      {isCreatingBranch ? 'Saving Location...' : existingBranch ? 'Update Location' : 'Save Location'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Step 2: Payment Method */}
            {setupStep === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Step 2: Payment Method
                  </Typography>
                  {!showAddPaymentForm && paymentMethods.length > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowAddPaymentForm(true)}
                      sx={{ 
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': { 
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: 'rgba(32, 104, 132, 0.04)'
                        }
                      }}
                    >
                      + Add New
                    </Button>
                  )}
                </Box>
                
                {!showAddPaymentForm && paymentMethods.length > 0 ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Your payment methods
                    </Typography>
                    
                    {/* List of payment methods */}
                    {paymentMethods.map((method, index) => {
                      const isDefault = index === 0; // First card is the default/selected one
                      return (
                        <Box 
                          key={method.id}
                          sx={{ 
                            border: isDefault ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            backgroundColor: isDefault ? '#f8f9fa' : 'transparent',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              backgroundColor: isDefault ? theme.palette.primary.main : '#e0e0e0',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <CreditCardIcon />
                            </Box>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {method.card?.brand?.toUpperCase()} •••• {method.card?.last4}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Expires {method.card?.exp_month}/{method.card?.exp_year}
                              </Typography>
                            </Box>
                          </Box>
                          {isDefault && (
                            <Box sx={{
                              backgroundColor: '#10b981',
                              color: 'white',
                              px: 2,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.875rem',
                              fontWeight: 600
                            }}>
                              Default
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                    
                    {/* Next button */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => handleBackToStep(0)}
                      >
                        ← Back
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => setSetupStep(2)}
                        sx={{ 
                          backgroundColor: theme.palette.primary.main,
                          '&:hover': { backgroundColor: theme.palette.primary.dark }
                        }}
                      >
                        Next: Select Plan
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Enter your payment information to continue.
                    </Typography>
                    <Elements stripe={stripePromise}>
                      <PaymentForm 
                        onSuccess={handlePaymentMethodComplete}
                      />
                    </Elements>
                    
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => handleBackToStep(0)}
                      >
                        ← Back
                      </Button>
                      {paymentMethods.length > 0 && (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => setShowAddPaymentForm(false)}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Step 3: Select Subscription Plan */}
            {setupStep === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Step 3: Select Your Subscription Plan
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Choose a plan that works best for you. Pricing is calculated based on {numberOfBranches} saved {numberOfBranches === 1 ? 'location' : 'locations'}.
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
                  {/* Monthly Plan */}
                  <Card 
                    sx={{ 
                      width: 280,
                      border: '3px solid',
                      borderColor: selectedPlan === 'monthly' ? theme.palette.primary.main : '#e5e7eb',
                      backgroundColor: selectedPlan === 'monthly' ? 'rgba(32, 104, 132, 0.05)' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}
                    onClick={() => handlePlanSelected('monthly')}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                        Monthly
                      </Typography>
                      <Box sx={{ my: 3 }}>
                        <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: '#1f2937' }}>
                          ${50 * numberOfBranches}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          per month
                        </Typography>
                        {numberOfBranches > 1 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            ${50} × {numberOfBranches} {numberOfBranches === 1 ? 'branch' : 'branches'}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Billed monthly
                      </Typography>
                      <Button 
                        variant={selectedPlan === 'monthly' ? 'contained' : 'outlined'}
                        fullWidth
                        sx={{ 
                          backgroundColor: selectedPlan === 'monthly' ? theme.palette.primary.main : 'transparent',
                          borderColor: theme.palette.primary.main,
                          color: selectedPlan === 'monthly' ? 'white' : theme.palette.primary.main,
                          '&:hover': { 
                            backgroundColor: selectedPlan === 'monthly' ? theme.palette.primary.dark : 'rgba(32, 104, 132, 0.04)',
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        {selectedPlan === 'monthly' ? '✓ Selected' : 'Select Plan'}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Annual Plan */}
                  <Card 
                    sx={{ 
                      width: 280,
                      border: '3px solid',
                      borderColor: selectedPlan === 'annual' ? theme.palette.primary.main : '#e5e7eb',
                      backgroundColor: selectedPlan === 'annual' ? 'rgba(32, 104, 132, 0.05)' : 'white',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}
                    onClick={() => handlePlanSelected('annual')}
                  >
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: -12,
                        right: 16,
                        backgroundColor: theme.palette.success.main,
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                    >
                      Save {Math.round(((50 * 12 - 420) / (50 * 12)) * 100)}%
                    </Box>
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                        Annual
                      </Typography>
                      <Box sx={{ my: 3 }}>
                        <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: '#1f2937' }}>
                          ${420 * numberOfBranches}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          per year
                        </Typography>
                        {numberOfBranches > 1 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            ${420} × {numberOfBranches} {numberOfBranches === 1 ? 'branch' : 'branches'}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Billed annually
                      </Typography>
                      <Button 
                        variant={selectedPlan === 'annual' ? 'contained' : 'outlined'}
                        fullWidth
                        sx={{ 
                          backgroundColor: selectedPlan === 'annual' ? theme.palette.primary.main : 'transparent',
                          borderColor: theme.palette.primary.main,
                          color: selectedPlan === 'annual' ? 'white' : theme.palette.primary.main,
                          '&:hover': { 
                            backgroundColor: selectedPlan === 'annual' ? theme.palette.primary.dark : 'rgba(32, 104, 132, 0.04)',
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        {selectedPlan === 'annual' ? '✓ Selected' : 'Select Plan'}
                      </Button>
                    </CardContent>
                  </Card>
                </Box>

                {/* Finish Button */}
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleBackToStep(1)}
                  >
                    ← Back
                  </Button>
                  <Button 
                    variant="contained"
                    fullWidth
                    onClick={handleFinishSetup}
                    disabled={!selectedPlan || isCreatingBranch}
                    sx={{ 
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                      '&:disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#999'
                      }
                    }}
                  >
                    {isCreatingBranch ? 'Creating Subscription...' : 'Complete Setup'}
                  </Button>
                </Box>
              </Box>
            )}

          </DialogContent>
        </Dialog>

        {/* Stripe Connect Setup Modal - Separate from subscription (z-index below Terms modal) */}
        <Dialog 
          open={showStripeConnectModal && !stripeConnectAccount && currentAccountType !== 'admin' && currentAccountType !== 'staff'}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
          scroll="body"
          sx={{
            zIndex: 1300,
            '& .MuiDialog-paper': {
              borderRadius: 2,
              maxHeight: 'none',
              overflow: 'visible'
            }
          }}
        >
          <DialogTitle sx={{ 
            textAlign: 'center', 
            pt: 4,
            pb: 2,
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2 
            }}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AccountBalanceIcon sx={{ fontSize: 48, color: '#2563eb' }} />
              </Box>
            </Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: '#1f2937' }}>
              Connect Your Stripe Account
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mt: 1 }}
            >
              Set up your Stripe Connect account to receive payments from customers
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ px: 4, pb: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This is required to use the platform. You'll need to provide business information and bank account details to receive payments from customers.
            </Typography>
            <StripeConnect 
              showAsCard={false}
              showTitle={false}
              onAccountUpdate={handleStripeConnectComplete}
            />
            
            {/* Manual continue button - only show after Stripe fires complete event */}
            {stripeOnboardingComplete && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleContinueToDashboard}
                  sx={{ 
                    backgroundColor: '#2c4575',
                    '&:hover': { backgroundColor: '#1e3154' }
                  }}
                >
                  Continue to Dashboard
                </Button>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardLayout;
